import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead, organizationSchema } from '../components/SEOHead';
import { 
  GraduationCap, 
  Users, 
  Globe, 
  Calendar,
  BookOpen,
  Heart,
  Award,
  Clock,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  MapPin
} from 'lucide-react';

// Hero images
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_arce-escuela/artifacts/6a7mrukk_image-asset.webp';
const STUDENTS_IMAGE = 'https://images.pexels.com/photos/5905955/pexels-photo-5905955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export default function LandingPage() {
  const { t, language } = useLanguage();
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Colegio ARCE — Escuela de Español Online para Niños Hispanohablantes en EE.UU."
        titleEN="Colegio ARCE — Online Spanish School for Heritage-Speaking Kids in the US"
        descES="Colegio ARCE es una escuela de español online fundada en 2023, con sede en Boston, MA. Clases virtuales en grupos reducidos para niños y jóvenes hispanohablantes en EE.UU., siguiendo el currículo oficial del Ministerio de Educación de España."
        descEN="Colegio ARCE is an online Spanish school founded in 2023, based in Boston, MA. Virtual small-group classes for heritage Spanish-speaking children and teens across the US, following the official Spanish Ministry of Education curriculum."
        path="/"
        schema={organizationSchema}
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' }
        ]}
      />
      <Navbar />
      
      {/* Hero Section - Split Layout */}
      <section className="relative min-h-screen pt-28 bg-gradient-to-br from-[#FFF5F5] via-white to-[#FFF0F0]">
        {/* World Map Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 500'%3E%3Cpath fill='%23E63946' d='M150,100 Q200,80 250,100 T350,90 L400,120 Q450,100 500,130 L480,180 Q420,200 380,180 T280,190 L200,160 Q160,140 150,100 M550,80 Q600,60 650,90 T750,70 L800,100 Q850,120 820,160 L780,180 Q720,160 680,180 T580,150 L550,120 Q530,100 550,80 M100,250 Q150,230 200,260 T300,240 L350,280 Q400,300 380,340 L320,360 Q260,340 220,360 T120,320 L100,280 Q80,260 100,250'/%3E%3C/svg%3E")`,
          backgroundSize: '100% 100%'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-7rem)]">
            
            {/* Left: Text Content */}
            <div className="order-2 lg:order-1 text-center lg:text-left py-12">
              {/* School Name */}
              <p className="text-sm tracking-[0.3em] uppercase text-[#E63946] font-semibold mb-6 fade-up" data-testid="hero-subtitle">
                Colegio ARCE Spanish School
              </p>
              
              {/* CTAs - Above tagline */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 mb-10 fade-up stagger-1">
                <Link
                  to="/login?redirect=/enrollment"
                  className="bg-[#E63946] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#D62828] transition-all shadow-lg flex items-center gap-2"
                  data-testid="hero-cta-register"
                >
                  {t('hero.cta1')}
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <a
                  href="https://scheduler.zoom.us/laura-pallares-nieto/informacion-colegio-arce"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-[#1D1D1F] border border-black/10 rounded-full px-7 py-3.5 font-medium hover:bg-[#F5F5F7] transition-all"
                  data-testid="hero-cta-appointment"
                >
                  {t('hero.cta2')}
                </a>
                <a
                  href="https://wa.me/13392277556"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white rounded-full px-5 py-3.5 font-medium hover:bg-[#1DA851] transition-all shadow-lg flex items-center gap-2"
                  data-testid="hero-cta-whatsapp"
                >
                  <MessageCircle className="h-5 w-5" />
                  WhatsApp
                </a>
              </div>
              
              {/* Tagline with elegant font */}
              <h1 
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-[#1D1D1F] leading-tight mb-8 fade-up stagger-2"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 400 }}
                data-testid="hero-tagline"
              >
                {language === 'es' 
                  ? <>De casa al mundo,<br /><span className="text-[#E63946]">tu español</span> crece con confianza</>
                  : <>From home to the world,<br /><span className="text-[#E63946]">your Spanish</span> grows with confidence</>
                }
              </h1>
              
              <p className="text-base sm:text-lg text-[#86868B] leading-relaxed mb-12 max-w-lg mx-auto lg:mx-0 fade-up stagger-3" data-testid="hero-description">
                {t('hero.subtitle')}
              </p>

              {/* Stats - School stats */}
              <div className="flex items-center justify-center lg:justify-start gap-6 sm:gap-8 fade-up stagger-4 flex-wrap">
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#E63946]">30+</p>
                  <p className="text-xs sm:text-sm text-[#86868B]">{language === 'es' ? 'Estudiantes' : 'Students'}</p>
                </div>
                <div className="w-px h-12 bg-black/10" />
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#E63946]">25</p>
                  <p className="text-xs sm:text-sm text-[#86868B]">{language === 'es' ? 'Familias' : 'Families'}</p>
                </div>
                <div className="w-px h-12 bg-black/10" />
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#E63946]">8</p>
                  <p className="text-xs sm:text-sm text-[#86868B]">{language === 'es' ? 'Max por clase' : 'Max per class'}</p>
                </div>
                <div className="w-px h-12 bg-black/10" />
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#E63946]">5</p>
                  <p className="text-xs sm:text-sm text-[#86868B]">{language === 'es' ? 'Paises representados' : 'Countries represented'}</p>
                </div>
                <div className="w-px h-12 bg-black/10" />
                <div className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#E63946]">100%</p>
                  <p className="text-xs sm:text-sm text-[#86868B]">{language === 'es' ? 'Profesores nativos de España' : 'Native Spanish teachers'}</p>
                </div>
              </div>
            </div>

            {/* Right: Child Image with Map Accent */}
            <div className="order-1 lg:order-2 relative fade-up">
              {/* Map dots decoration */}
              <div className="absolute -top-8 -right-8 w-64 h-64 opacity-20">
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  {/* Spain */}
                  <circle cx="95" cy="45" r="4" fill="#E63946" />
                  {/* Mexico */}
                  <circle cx="35" cy="70" r="5" fill="#E63946" />
                  {/* Colombia */}
                  <circle cx="55" cy="105" r="4" fill="#E63946" />
                  {/* Argentina */}
                  <circle cx="65" cy="155" r="5" fill="#E63946" />
                  {/* Peru */}
                  <circle cx="50" cy="120" r="3" fill="#E63946" />
                  {/* Chile */}
                  <circle cx="55" cy="145" r="3" fill="#E63946" />
                  {/* USA - Hispanic community */}
                  <circle cx="40" cy="55" r="4" fill="#E63946" opacity="0.6" />
                  {/* Connection lines */}
                  <path d="M95,45 Q70,60 55,105" stroke="#E63946" strokeWidth="0.5" fill="none" opacity="0.3" />
                  <path d="M35,70 Q45,90 55,105" stroke="#E63946" strokeWidth="0.5" fill="none" opacity="0.3" />
                </svg>
              </div>
              
              {/* Main Image */}
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl bg-white p-2">
                  <img 
                    src={HERO_IMAGE} 
                    alt="Student" 
                    fetchPriority="high"
                    className="w-full h-[500px] lg:h-[600px] object-cover object-top rounded-2xl"
                    data-testid="hero-image"
                  />
                </div>
                
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl border border-black/5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#E63946]/10 flex items-center justify-center">
                      <Globe className="h-6 w-6 text-[#E63946]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1D1D1F] text-sm">{language === 'es' ? 'Comunidad Global' : 'Global Community'}</p>
                      <p className="text-xs text-[#86868B]">{language === 'es' ? '2° idioma más hablado' : '2nd most spoken language'}</p>
                    </div>
                  </div>
                </div>

                {/* Location badge */}
                <div className="absolute -top-4 -right-4 bg-[#E63946] text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm font-medium">Medford, MA</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Grid - What We Offer */}
      <section className="section-padding bg-white" ref={addToRefs}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal" ref={addToRefs}>
            <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
              {language === 'es' ? 'Nuestra Propuesta' : 'Our Approach'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F]" data-testid="what-we-offer-title">
              {t('sections.whatWeOffer')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Large Card */}
            <div className="md:col-span-2 bento-item reveal" ref={addToRefs} data-testid="methodology-card">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="flex-1">
                  <BookOpen className="h-10 w-10 text-[#E63946] mb-4" />
                  <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3">{t('sections.methodology')}</h3>
                  <p className="text-[#86868B] leading-relaxed">{t('sections.methodologyDesc')}</p>
                </div>
                <div className="w-full md:w-48 h-48 rounded-2xl overflow-hidden">
                  <img src={STUDENTS_IMAGE} alt="Students" loading="lazy" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            {/* Small Card */}
            <div className="bento-item reveal" ref={addToRefs} data-testid="philosophy-card">
              <Heart className="h-10 w-10 text-[#E63946] mb-4" />
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">{t('sections.philosophy')}</h3>
              <p className="text-[#86868B] leading-relaxed text-sm">{t('sections.philosophyDesc')}</p>
            </div>

            {/* Small Card */}
            <div className="bento-item reveal" ref={addToRefs} data-testid="offer-card">
              <Clock className="h-10 w-10 text-[#E63946] mb-4" />
              <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">{language === 'es' ? 'Clases Semanales' : 'Weekly Classes'}</h3>
              <p className="text-[#86868B] leading-relaxed text-sm">{t('sections.whatWeOfferDesc')}</p>
            </div>

            {/* Medium Card */}
            <Link to={`/${language}/${language === 'es' ? 'calendario' : 'calendar'}`} className="md:col-span-2 bento-item reveal hover:border-[#E63946]/30 transition-colors cursor-pointer" ref={addToRefs} data-testid="calendar-card">
              <div className="flex items-start gap-6">
                <Calendar className="h-10 w-10 text-[#E63946] flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3">{t('sections.calendar')}</h3>
                  <p className="text-[#86868B] leading-relaxed">{t('sections.calendarDesc')}</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-[#1D1D1F]">
                      {language === 'es' ? 'Trimestre 1: Sep-Nov 2026' : 'Trimester 1: Sep-Nov 2026'}
                    </span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-[#1D1D1F]">
                      {language === 'es' ? 'Trimestre 2: Nov 2026-Feb 2027' : 'Trimester 2: Nov 2026-Feb 2027'}
                    </span>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-[#1D1D1F]">
                      {language === 'es' ? 'Trimestre 3: Mar-May 2027' : 'Trimester 3: Mar-May 2027'}
                    </span>
                  </div>
                  <p className="mt-3 text-xs text-[#86868B]">
                    {language === 'es' ? '30 semanas · 10 clases por trimestre · Clases presenciales (MA) incluidas' : '30 weeks · 10 classes per trimester · In-person classes (MA) included'}
                  </p>
                  <p className="mt-2 text-sm text-[#E63946] font-medium">{language === 'es' ? 'Ver calendario completo →' : 'View full calendar →'}</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-[#F5F5F7]" ref={addToRefs}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal" ref={addToRefs}>
            <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
              {language === 'es' ? '¿Por qué elegirnos?' : 'Why Choose Us?'}
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F]" data-testid="why-choose-title">
              {language === 'es' ? 'La excelencia en la enseñanza del español' : 'Excellence in Spanish Teaching'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: GraduationCap,
                title: language === 'es' ? 'Currículo Oficial' : 'Official Curriculum',
                desc: language === 'es' ? 'Programa del Ministerio de Educación de España' : 'Spanish Ministry of Education curriculum'
              },
              {
                icon: Users,
                title: language === 'es' ? 'Grupos Reducidos' : 'Small Groups',
                desc: language === 'es' ? 'Máximo 8 estudiantes por clase' : 'Maximum 8 students per class'
              },
              {
                icon: Globe,
                title: language === 'es' ? '15+ Años de Experiencia' : '15+ Years Experience',
                desc: language === 'es' ? 'Profesores nativos con décadas de experiencia' : 'Native teachers with decades of experience'
              },
              {
                icon: Clock,
                title: language === 'es' ? '30 Semanas al Año' : '30 Weeks per Year',
                desc: language === 'es' ? 'Sesiones de 50 min, 3 trimestres' : '50 min sessions, 3 trimesters'
              }
            ].map((feature, index) => (
              <div key={index} className="card-apple p-8 text-center reveal" ref={addToRefs} data-testid={`feature-card-${index}`}>
                <div className="w-16 h-16 rounded-2xl bg-[#E63946]/10 flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="h-8 w-8 text-[#E63946]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1D1D1F] mb-3">{feature.title}</h3>
                <p className="text-[#86868B] text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Hidden until real testimonials are collected */}
      {/* 
      <section className="section-padding bg-white" ref={addToRefs}>
        ... testimonials section hidden ...
      </section>
      */}

      {/* Pricing Preview */}
      <section className="section-padding bg-white" ref={addToRefs}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal" ref={addToRefs}>
            <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
              {t('pricing.title')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#1D1D1F] mb-4" data-testid="pricing-title">
              {t('pricing.subtitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Annual Plan (dynamic: Early Bird or Normal based on date) */}
            {(() => {
              const now = new Date();
              const m = now.getMonth() + 1;
              const isEB = m >= 5 && m <= 6;
              const tierLabel = isEB ? t('pricing.earlyBird') : t('pricing.normal');
              const tierPrice = isEB ? 1390 : 1495;
              const tierPeriod = isEB ? t('pricing.earlyBirdPeriod') : t('pricing.normalPeriod');
              const tierParam = isEB ? 'early_bird' : 'normal';
              return (
                <div className="card-apple p-8 relative reveal" ref={addToRefs} data-testid={`pricing-${tierParam}`}>
                  {isEB && (
                    <div className="absolute -top-3 left-6">
                      <span className="inline-flex items-center gap-1 bg-[#34C759] text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {t('pricing.bestValue')}
                      </span>
                    </div>
                  )}
                  <p className="text-sm tracking-wide uppercase text-[#86868B] mb-2 mt-2">{tierLabel}</p>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold text-[#1D1D1F]">${tierPrice.toLocaleString()}</span>
                    <span className="text-[#86868B]">{t('pricing.perYear')}</span>
                  </div>
                  <p className="text-sm text-[#86868B] mb-6">{tierPeriod}</p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.annual')}</span></li>
                    <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.installments')}</span></li>
                    <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.monthly')}</span></li>
                    <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.siblingDiscount')}</span></li>
                  </ul>
                  <Link to={`/login?redirect=/enrollment?tier=${tierParam}`} className="btn-primary w-full text-center block" data-testid={`pricing-${tierParam}-cta`}>
                    {t('pricing.enroll')}
                  </Link>
                </div>
              );
            })()}

            {/* Trimestral */}
            <div className="card-apple p-8 reveal relative" ref={addToRefs} data-testid="pricing-trimestral">
              <div className="absolute -top-3 left-6">
                <span className="inline-flex items-center gap-1 bg-[#007AFF] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t('pricing.flexible')}
                </span>
              </div>
              <p className="text-sm tracking-wide uppercase text-[#86868B] mb-2 mt-2">{t('pricing.trimestral')}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-[#1D1D1F]">$620</span>
                <span className="text-[#86868B]">{t('pricing.perTrimester')}</span>
              </div>
              <p className="text-xs text-[#FF9500] font-medium mb-1">{t('pricing.trimestralTotal')}</p>
              <p className="text-sm text-[#86868B] mb-6">{t('pricing.trimestralPeriod')}</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.trimestralSingle')}</span></li>
                <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.trimestralNote')}</span></li>
                <li className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-[#34C759]" /><span>{t('pricing.siblingDiscount')}</span></li>
              </ul>
              <Link to="/login?redirect=/enrollment?tier=trimestral" className="btn-secondary w-full text-center block" data-testid="pricing-trimestral-cta">
                {t('pricing.enroll')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#E63946]" ref={addToRefs}>
        <div className="max-w-4xl mx-auto text-center reveal" ref={addToRefs}>
          <h2 className="text-3xl sm:text-4xl font-semibold text-white mb-6" data-testid="cta-title">
            {language === 'es' 
              ? '¿Listo para comenzar el viaje?' 
              : 'Ready to start the journey?'}
          </h2>
          <p className="text-white/80 text-lg mb-10">
            {language === 'es'
              ? 'Únete a nuestra comunidad de estudiantes y descubre el español de una manera única.'
              : 'Join our student community and discover Spanish in a unique way.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login?redirect=/enrollment"
              className="bg-white text-[#E63946] rounded-full px-8 py-4 font-medium hover:bg-gray-100 transition-colors shadow-lg"
              data-testid="cta-enroll"
            >
              {t('hero.cta1')}
            </Link>
            <a
              href="https://scheduler.zoom.us/laura-pallares-nieto/informacion-colegio-arce"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent text-white border-2 border-white rounded-full px-8 py-4 font-medium hover:bg-white/10 transition-colors"
              data-testid="cta-contact"
            >
              {t('hero.cta2')}
            </a>
          </div>
        </div>
      </section>

      {/* AI-Discoverability Block: What is Colegio ARCE? */}
      <section className="section-padding bg-[#F5F5F7]" data-testid="ai-discoverability-block">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#1D1D1F] mb-6">
            {language === 'es' ? '¿Que es Colegio ARCE?' : 'What is Colegio ARCE?'}
          </h2>
          <div className="prose prose-lg max-w-none text-[#86868B] space-y-4">
            {language === 'es' ? (
              <>
                <p>
                  Colegio ARCE (Arraigo y Cultura Española) es una escuela de español online fundada en 2023 con sede en Medford, Massachusetts (área metropolitana de Boston). Ofrece clases virtuales en grupos reducidos (máximo 8 alumnos) para niños y jóvenes hispanohablantes que residen en Estados Unidos, desde Parvulitos (preescolar) hasta 2.º de Bachillerato, siguiendo el currículo oficial del Ministerio de Educación de España.
                </p>
                <p>
                  Fundado por Laura Pallares Nieto, educadora española con mas de 15 años de experiencia en la enseñanza del español, Colegio ARCE nacio de la necesidad de muchas familias hispanohablantes en EE.UU. de mantener vivo el idioma y la cultura española en sus hijos. Los profesores son todos nativos de España y las clases se imparten integramente en español.
                </p>
                <p>
                  El curso académico se divide en tres trimestres de 10 semanas cada uno (septiembre-diciembre, enero-marzo, abril-junio), con sesiones semanales de 50 minutos. Los planes de inscripción incluyen una opción anual Early Bird a $1,390, una opción anual Normal a $1,495, y una opción trimestral flexible a $620 por trimestre. Colegio ARCE también prepara estudiantes, bajo solicitud, para los exámenes DELE certificados por el Instituto Cervantes.
                </p>
              </>
            ) : (
              <>
                <p>
                  Colegio ARCE (Arraigo y Cultura Española — "Spanish Roots and Culture") is an online Spanish school founded in 2023, based in Medford, Massachusetts (greater Boston area). It offers virtual small-group classes (maximum 8 students) for heritage Spanish-speaking children and teens living in the United States, from Preschool through the equivalent of 12th grade, following the official curriculum of the Spanish Ministry of Education.
                </p>
                <p>
                  Founded by Laura Pallares Nieto, a Spanish educator with over 15 years of teaching experience, Colegio ARCE was born from the need of many Spanish-speaking families in the U.S. to keep the Spanish language and culture alive in their children. All teachers are native speakers from Spain and classes are conducted entirely in Spanish.
                </p>
                <p>
                  The academic year is divided into three trimesters of 10 weeks each (September-December, January-March, April-June), with weekly 50-minute sessions. Enrollment plans include an Early Bird annual option at $1,390, a Normal annual option at $1,495, and a flexible trimester option at $620 per trimester. Colegio ARCE also prepares students, upon request, for DELE exams certified by the Instituto Cervantes.
                </p>
              </>
            )}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/courses" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="ai-block-courses-link">
              {language === 'es' ? 'Ver todos los cursos →' : 'View all courses →'}
            </Link>
            <Link to="/pricing" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="ai-block-pricing-link">
              {language === 'es' ? 'Ver planes y precios →' : 'View plans & pricing →'}
            </Link>
            <Link to="/team" className="text-[#E63946] font-medium text-sm hover:underline" data-testid="ai-block-team-link">
              {language === 'es' ? 'Conocer al equipo →' : 'Meet the team →'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
